import {
  fetchSpottedNotSecured,
  createOneSpottedNote,
  countSpottedNotSecuredByReactStatus,
  fetchSpottedNoteList,
  fetchSpottedNotSecuredMapData,
  GraphQLClient,
  countSpottedNotSecuredByOrderType,
  countSpottedNotSecured,
  SpottedNote,
} from '@rapid-recovery-agency-inc/insightt-shared';
import { createAsyncThunk, createAction } from '@reduxjs/toolkit';

import { AppState } from '../../redux/store';
import { InsighttNotification } from '../../helpers/insightt-notification';
import { client, evictRootQueryCache } from '../../shared/apollo';

import {
  FETCH_SNS_ERROR_MSG,
  FETCH_SNS_THUNK_TYPE,
  CREATE_SPOTTED_NOTE_ERROR_MSG,
  CREATE_SPOTTED_NOTE_THUNK_TYPE,
  PAGINATION_TAKE_AMOUNT,
  SPOTTED_NOT_SECURED_MODAL_TYPE,
  SPOTTED_NOT_SECURED_ON_SEARCH_TYPE,
  SPOTTED_NOT_SECURED_STATE_TYPE,
  FETCH_SPOTTED_NOTES_THUNK_TYPE,
  FETCH_SPOTTED_NOTE_ERROR_MSG,
  TAKE_CASE_NOTES_AMOUNT,
  SPOTTED_NOT_SECURED_ON_BRANCH_CHANGE_TYPE,
  SPOTTED_NOT_SECURED_HANDLE_SEARCH_TYPE,
  SPOTTED_NOT_SECURED_LOADING,
  COUNT_ORDER_TYPE_THUNK_TYPE,
  COUNT_ORDER_TYPE_ERROR_MSG,
  COUNT_RHL_ERROR_MSG,
  COUNT_RHL_THUNK_TYPE,
  COUNT_REACH_STATUS_THUNK_TYPE,
  COUNT_REACH_STATUS_ERROR_MSG,
  FETCH_SNS_MAP_DATA_THUNK_TYPE,
  FETCH_SNS_MAP_ERROR_MSG,
  REFETCH_SNS_THUNK_TYPE,
  REFETCH_SNS_ERROR_MSG,
  EDIT_SPOTTED_NOTE_THUNK_TYPE,
  EDIT_SPOTTED_NOTE_ERROR_MSG,
  CHANGE_COLLATERAL_COLOR_TYPE,
} from './spotted-not-secured-constants';
import {
  HandleOnSearchParams,
  FetchRepoHitListActionParams,
  CreateSpottedNoteParams,
  FetchSpottedNotesParams,
} from './spotted-not-secured-types';
import {
  deleteSpottedNoteInSNS,
  updateSpottedNoteInSNS,
  updateSpottedNoteOnCase,
} from './spotted-not-secured-utils';
import { RdnCase } from '@rapid-recovery-agency-inc/insightt-graphql';
import { APOLLO_ROOT_QUERY_FIELD_NAMES } from '../../constants/app.constants';

const apolloClient = client as GraphQLClient;

export const setLoading = createAction<boolean>(SPOTTED_NOT_SECURED_LOADING);

export const setVehicleModal = createAction<Record<string, unknown> | null>(SPOTTED_NOT_SECURED_MODAL_TYPE);

export const onFilterChange = createAction(SPOTTED_NOT_SECURED_STATE_TYPE);

export const onSearch = createAction<string | null | undefined>(SPOTTED_NOT_SECURED_ON_SEARCH_TYPE);

export const fetchMapData = createAsyncThunk(
  FETCH_SNS_MAP_DATA_THUNK_TYPE,
  async (data: FetchRepoHitListActionParams, thunkApi) => {
    try {
      const response = await fetchSpottedNotSecuredMapData(apolloClient, data);

      if (!response) throw new Error(FETCH_SNS_MAP_ERROR_MSG);

      return thunkApi.fulfillWithValue(response);
    } catch {
      InsighttNotification('error', FETCH_SNS_MAP_ERROR_MSG);
      return thunkApi.rejectWithValue(FETCH_SNS_MAP_ERROR_MSG);
    }
  },
);

export const countReachStatus = createAsyncThunk(
  COUNT_REACH_STATUS_THUNK_TYPE,
  async (data: FetchRepoHitListActionParams, thunkApi) => {
    try {
      const response = await countSpottedNotSecuredByReactStatus(apolloClient, data);

      if (!response) throw new Error(COUNT_REACH_STATUS_ERROR_MSG);

      return thunkApi.fulfillWithValue({
        obstructedCount: response?.obstructed,
        accessibleCount: response?.accessible,
      });
    } catch {
      InsighttNotification('error', COUNT_REACH_STATUS_ERROR_MSG);
      return thunkApi.rejectWithValue(COUNT_REACH_STATUS_ERROR_MSG);
    }
  },
);

export const countOrderType = createAsyncThunk(
  COUNT_ORDER_TYPE_THUNK_TYPE,
  async (data: FetchRepoHitListActionParams, thunkApi) => {
    try {
      const response = await countSpottedNotSecuredByOrderType(apolloClient, data);

      if (!response) throw new Error(COUNT_ORDER_TYPE_ERROR_MSG);

      return thunkApi.fulfillWithValue({
        allOrderTypeCount: response?.all,
        voluntaryCount: response?.voluntary,
        involuntaryCount: response?.involuntary,
        impoundCount: response?.impound,
      });
    } catch {
      InsighttNotification('error', COUNT_ORDER_TYPE_ERROR_MSG);
      return thunkApi.rejectWithValue(COUNT_ORDER_TYPE_ERROR_MSG);
    }
  },
);

export const fetchSpottedNotesAction = createAsyncThunk(
  FETCH_SPOTTED_NOTES_THUNK_TYPE,
  async (data: FetchSpottedNotesParams, thunkApi) => {
    try {
      const response = await fetchSpottedNoteList(apolloClient, {
        caseId: data.caseId,
        orderBy: 'desc',
      });

      if (!response) throw new Error(FETCH_SPOTTED_NOTE_ERROR_MSG);

      return thunkApi.fulfillWithValue({ spottedNotes: response });
    } catch {
      InsighttNotification('error', FETCH_SPOTTED_NOTE_ERROR_MSG);
      return thunkApi.rejectWithValue(FETCH_SPOTTED_NOTE_ERROR_MSG);
    }
  },
);

export const countListAction = createAsyncThunk(
  COUNT_RHL_THUNK_TYPE,
  async (data: FetchRepoHitListActionParams, thunkApi) => {
    try {
      const response = await countSpottedNotSecured(apolloClient, data);

      if (!response) throw new Error(COUNT_RHL_ERROR_MSG);

      return thunkApi.fulfillWithValue(response.count);
    } catch {
      InsighttNotification('error', COUNT_RHL_ERROR_MSG);
      return thunkApi.rejectWithValue(COUNT_RHL_ERROR_MSG);
    }
  },
);

export const fetchListAction = createAsyncThunk(
  FETCH_SNS_THUNK_TYPE,
  async (data: FetchRepoHitListActionParams, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const { skipAmount, listData } = state.spottedNotSecured;

    // don't show loading when loading more or searching
    const { isLoadMore } = data;
    if (!isLoadMore) thunkApi.dispatch(setLoading(true));

    try {
      const promises = [
        fetchSpottedNotSecured(apolloClient, {
          takeNotes: TAKE_CASE_NOTES_AMOUNT,
          skip: skipAmount,
          take: PAGINATION_TAKE_AMOUNT,
          ...data,
        }),
        countSpottedNotSecured(apolloClient, data),
      ];

      const [list, countResult] = await Promise.all(promises);

      if (!list) throw new Error(FETCH_SNS_ERROR_MSG);

      const result = data?.hasSearched ? list : [...listData, ...list];

      return thunkApi.fulfillWithValue({
        listData: result,
        skipAmount: skipAmount + PAGINATION_TAKE_AMOUNT,
        listCount: countResult?.count,
      });
    } catch {
      InsighttNotification('error', FETCH_SNS_ERROR_MSG);
      return thunkApi.rejectWithValue(FETCH_SNS_ERROR_MSG);
    }
  },
);

export const createSpottedNoteAction = createAsyncThunk(
  CREATE_SPOTTED_NOTE_THUNK_TYPE,
  async (data: CreateSpottedNoteParams, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const currentState = state.spottedNotSecured;
    const spottedNotes = currentState.vehicleModalSpottedNotesData ?? [];

    try {
      const response = await createOneSpottedNote(client, data);
      if (!response) throw new Error(CREATE_SPOTTED_NOTE_ERROR_MSG);

      const list = updateSpottedNoteOnCase({
        caseId: data.caseId,
        list: currentState.listData,
        spottedNote: response,
      });

      return thunkApi.fulfillWithValue({
        list,
        vehicleModalSpottedNotesData: [response, ...spottedNotes],
      });
    } catch {
      InsighttNotification('error', CREATE_SPOTTED_NOTE_ERROR_MSG);
      return thunkApi.rejectWithValue(CREATE_SPOTTED_NOTE_ERROR_MSG);
    }
  },
);

export const handleOnFilterChange = createAsyncThunk(
  SPOTTED_NOT_SECURED_ON_BRANCH_CHANGE_TYPE,
  async (data: FetchRepoHitListActionParams, thunkApi) => {
    const { dispatch } = thunkApi;

    dispatch(onFilterChange());
    dispatch(fetchListAction(data));
  },
);

type EditSpottedNoteActionData = {
  caseId: number;
  spottedNote: SpottedNote;
};
export const editSpottedNoteAction = createAsyncThunk(
  EDIT_SPOTTED_NOTE_THUNK_TYPE,
  async (data: EditSpottedNoteActionData, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const currentSpottedNotSecured = state.spottedNotSecured;
    const currentModalSpottedNotes = currentSpottedNotSecured.vehicleModalSpottedNotesData || [];

    try {
      const { updatedSecuredNotSpotted } = updateSpottedNoteInSNS({
        caseId: data.caseId,
        currentModalSpottedNotes,
        spottedNote: data.spottedNote,
        currentSpottedNotSecured,
      });

      return thunkApi.fulfillWithValue({
        spottedNotSecured: updatedSecuredNotSpotted,
      });
    } catch {
      InsighttNotification('error', EDIT_SPOTTED_NOTE_ERROR_MSG);
      return thunkApi.rejectWithValue(EDIT_SPOTTED_NOTE_ERROR_MSG);
    }
  },
);

type DeleteSpottedNoteActionData = {
  caseId: number;
  spottedNote: SpottedNote;
};
export const deleteSpottedNoteAction = createAsyncThunk(
  EDIT_SPOTTED_NOTE_THUNK_TYPE,
  async (data: DeleteSpottedNoteActionData, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const currentSpottedNotSecured = state.spottedNotSecured;
    const currentModalSpottedNotes = currentSpottedNotSecured.vehicleModalSpottedNotesData || [];

    try {
      const { updatedSecuredNotSpotted } = deleteSpottedNoteInSNS({
        caseId: data.caseId,
        currentModalSpottedNotes,
        spottedNote: data.spottedNote,
        currentSpottedNotSecured,
      });

      return thunkApi.fulfillWithValue({
        spottedNotSecured: updatedSecuredNotSpotted,
      });
    } catch {
      InsighttNotification('error', EDIT_SPOTTED_NOTE_ERROR_MSG);
      return thunkApi.rejectWithValue(EDIT_SPOTTED_NOTE_ERROR_MSG);
    }
  },
);

export const handleRefetch = createAsyncThunk(
  REFETCH_SNS_THUNK_TYPE,
  async (data: FetchRepoHitListActionParams, thunkApi) => {
    const state = thunkApi.getState() as AppState;
    const { listData } = state.spottedNotSecured;
    try {
      const promises = [
        fetchSpottedNotSecured(apolloClient, {
          takeNotes: TAKE_CASE_NOTES_AMOUNT,
          skip: 0,
          take: listData?.length,
          fetchPolicy: 'network-only',
          ...data,
        }),
        countSpottedNotSecured(apolloClient, {
          ...data,
          fetchPolicy: 'network-only',
        }),
      ];

      const [list, count] = await Promise.all(promises);

      if (!list) throw new Error(REFETCH_SNS_ERROR_MSG);

      return thunkApi.fulfillWithValue({ list, count });
    } catch {
      InsighttNotification('error', REFETCH_SNS_ERROR_MSG);
      return thunkApi.rejectWithValue(REFETCH_SNS_ERROR_MSG);
    }
  },
);

export const handleEdit = createAsyncThunk(
  SPOTTED_NOT_SECURED_ON_BRANCH_CHANGE_TYPE,
  async (data: FetchRepoHitListActionParams, thunkApi) => {
    const { dispatch } = thunkApi;

    dispatch(countReachStatus({ ...data, fetchPolicy: 'network-only' }));
    dispatch(handleRefetch(data));
  },
);

export const handleOnSearch = createAsyncThunk(
  SPOTTED_NOT_SECURED_HANDLE_SEARCH_TYPE,
  async (data: HandleOnSearchParams, thunkApi) => {
    const { dispatch } = thunkApi;
    dispatch(onSearch(data.searchQuery));
    dispatch(
      fetchListAction({
        ...data,
        hasSearched: true,
      }),
    );
  },
);

export const updateOneColorInSNSAction = createAsyncThunk(
  CHANGE_COLLATERAL_COLOR_TYPE,
  async (
    data: {
      caseId: number;
      color: string | undefined;
    },
    thunkApi,
  ) => {
    const state = thunkApi.getState() as AppState;
    const listData = state.spottedNotSecured.listData as RdnCase[];

    evictRootQueryCache(APOLLO_ROOT_QUERY_FIELD_NAMES.rDNCases);

    const updatedListData = listData.map((caseData) => {
      if (caseData.caseId === data.caseId) {
        return {
          ...caseData,
          vehicleColor: data.color,
          collateral: {
            ...caseData.collateral,
            dynamicColor: {
              color: data.color,
            },
          },
        };
      }
      return caseData;
    });

    return thunkApi.fulfillWithValue({
      repoHitList: updatedListData,
    });
  },
);
